import {
  Component,
  DoCheck,
  EventEmitter,
  Input,
  IterableDiffers,
  OnInit,
  Output
} from '@angular/core'
import {
  EntityGroupFeeScheduleODataService,
  IEntityGroupFeeSchedule,
  IFeeSchedule
} from '@exchange/domain/.'
import { UtilService } from '@exchange/services/.'
import * as vis from 'vis'
import moment from 'moment'
import _ from 'lodash'
import { ToastrService } from 'ngx-toastr'

interface IFeeScheduleTimelineItem extends vis.TimelineItem {
  minDate?: vis.DateType
  maxDate?: vis.DateType
}

@Component({
  selector: 'app-entity-group-edit-feeschedule-timeline',
  templateUrl: './entity-group-edit-feeschedule-timeline.component.html',
  styleUrls: ['./entity-group-edit-feeschedule-timeline.component.scss'],
  standalone: false
})
export class EntityGroupEditFeescheduleTimelineComponent implements OnInit, DoCheck {
  @Input() entityGroupFeeSchedules: IEntityGroupFeeSchedule[] = []
  @Output() viewDetails = new EventEmitter<IFeeSchedule>()

  differ: any
  timeline: vis.Timeline = null
  ordinal = 1
  timelineItems: vis.DataSet<IFeeScheduleTimelineItem>
  selectedItem: IFeeScheduleTimelineItem

  constructor(
    public utilService: UtilService,
    differs: IterableDiffers,
    private entityGroupFeeScheduleService: EntityGroupFeeScheduleODataService,
    private toastr: ToastrService
  ) {
    // http://blog.bogdancarpean.com/how-to-watch-for-changes-an-arrayobject-on-angular-2/
    this.differ = differs.find([]).create(null)
  }

  ngOnInit() {
    this.resetSelectedItem()
    this.initializeDataSet()
    this.initializeTimeline()
  }

  ngDoCheck() {
    const change = this.differ.diff(this.entityGroupFeeSchedules)
    if (change && this.timelineItems) {
      change.forEachAddedItem(x => {
        this.timelineItems.add(this.convertFeeScheduleToTimeline(x.item))
      })
      change.forEachRemovedItem(x => {
        this.timelineItems.remove(x.item.Id)
      })

      this.markCollisionsAndGaps()
      this.resetZoom()
    }
  }

  initializeDataSet() {
    this.timelineItems = new vis.DataSet(
      this.entityGroupFeeSchedules.map(x =>
        this.convertFeeScheduleToTimeline(x)
      ) as IFeeScheduleTimelineItem[],
      { fieldId: 'id' }
    )
  }

  initializeTimeline() {
    const container = document.getElementById('visualization')

    const options: vis.TimelineOptions = {
      minHeight: '300px',
      orientation: 'top',
      margin: {
        axis: 30
      },
      start: moment.utc().add(-6, 'months').startOf('month').format('MM/DD/YYYY'),
      end: moment.utc().add(6, 'months').startOf('month').format('MM/DD/YYYY'),
      tooltipOnItemUpdateTime: true,
      stack: true,
      editable: {
        add: false,
        remove: false,
        updateGroup: false,
        updateTime: true,
        overrideItems: false
      },
      onMoving: this.onMoving.bind(this),
      onMove: this.onMove.bind(this)
    }

    this.timeline = new vis.Timeline(container, this.timelineItems, options)
    this.timeline.on('select', this.onSelect.bind(this))
  }

  onMove(item, cb) {
    const selectedEntityGroupFeeSchedule = this.entityGroupFeeSchedules.find(x => x.Id === item.id)
    const selectedTimelineItem = this.timelineItems.get(item.id, null)
    const changedData = {
      Start: (item.start < item.minDate ? item.minDate : item.start) as Date,
      End: (item.end > item.maxDate ? item.maxDate : item.end) as Date
    }

    this.entityGroupFeeScheduleService
      .put(
        Object.assign({}, selectedEntityGroupFeeSchedule, {
          Start: item.start,
          End: item.end
        }),
        selectedEntityGroupFeeSchedule.Id
      )
      .exec()
      .subscribe(
        () => {
          selectedEntityGroupFeeSchedule.Start = changedData.Start
          selectedEntityGroupFeeSchedule.End = changedData.End
          selectedTimelineItem.start = changedData.Start
          selectedTimelineItem.end = changedData.End
          item.start = changedData.Start
          item.end = changedData.End
          item.content = this.getFormattedContent(
            selectedEntityGroupFeeSchedule.FeeSchedule.Name,
            item.start,
            item.end
          )
          this.timelineItems.update(item, 'internal')
          this.markCollisionsAndGaps()
          this.resetZoom()
          this.toastr.success('Fee Schedule timeline moved')
          cb(item)
        },
        () => {
          this.toastr.error('Fee Schedule timeline could not be moved')
          cb(null)
        }
      )
  }

  onMoving(item, cb) {
    // Prevent user from sizing items outside their min/max values as defined by the fee schedule
    if (item.start < item.minDate) {
      item.start = item.minDate
    }

    if (item.end > item.maxDate) {
      item.end = item.maxDate
    }

    const feeScheduleName = this.entityGroupFeeSchedules.find(x => x.Id === this.selectedItem.id)
      .FeeSchedule.Name

    item.content = this.getFormattedContent(feeScheduleName, item.start, item.end)

    cb(item)
  }

  onSelect({ items, event }) {
    this.resetSelectedItem()

    const selectedGuid = items[0]

    if (selectedGuid) {
      this.selectedItem = this.timelineItems.get(selectedGuid as vis.IdType)

      const feeScheduleName = this.entityGroupFeeSchedules.find(x => x.Id === this.selectedItem.id)
        .FeeSchedule.Name

      const newBackground = {
        id: 'minMaxRange',
        start: this.selectedItem.minDate,
        end: this.selectedItem.maxDate,
        content: this.getFormattedContent(
          feeScheduleName,
          this.selectedItem.minDate,
          this.selectedItem.maxDate
        ),
        type: 'background',
        className: 'marker boundaries'
      } as IFeeScheduleTimelineItem

      this.timelineItems.remove('minMaxRange', 'internal')
      this.timelineItems.add(newBackground, 'internal')
    }
  }

  removeSelectedItem() {
    const id = this.selectedItem.id
    this.entityGroupFeeScheduleService
      .delete(id)
      .exec()
      .subscribe(
        () => {
          _.remove(this.entityGroupFeeSchedules, x => x.Id === id)
          this.resetSelectedItem()
          this.toastr.success('Fee Schedule removed')
        },
        () => {
          this.toastr.error('Fee Schedule could not be removed')
        }
      )
  }

  timelineSelectedItem_dateChanged() {
    const item = this.selectedItem

    if (item === null) {
      return
    }
    const selectedTimelineItem = this.timelineItems.get(item.id, null)

    const selectedEntityGroupFeeSchedule = this.entityGroupFeeSchedules.find(x => x.Id === item.id)

    const changedData = {
      Start: (item.start < item.minDate ? item.minDate : item.start) as Date,
      End: (item.end > item.maxDate ? item.maxDate : item.end) as Date
    }

    this.entityGroupFeeScheduleService
      .put(
        Object.assign({}, selectedEntityGroupFeeSchedule, changedData),
        selectedEntityGroupFeeSchedule.Id
      )
      .exec()
      .subscribe(
        () => {
          selectedEntityGroupFeeSchedule.Start = changedData.Start
          selectedEntityGroupFeeSchedule.End = changedData.End
          selectedTimelineItem.start = changedData.Start
          selectedTimelineItem.end = changedData.End
          item.start = changedData.Start
          item.end = changedData.End
          item.content = this.getFormattedContent(
            selectedEntityGroupFeeSchedule.FeeSchedule.Name,
            item.start,
            item.end
          )
          this.timelineItems.update(item, 'internal')
          this.markCollisionsAndGaps()
          this.resetZoom()
          this.toastr.success('Fee Schedule date was changed')
        },
        () => {
          this.toastr.error('Fee Schedule date could not be changed')
        }
      )
  }

  showSelectedItemDetails() {
    const selectedEntityGroupFeeSchedule = this.entityGroupFeeSchedules.find(
      x => x.Id === this.selectedItem.id
    )
    this.viewDetails.emit(selectedEntityGroupFeeSchedule.FeeSchedule)
  }

  resetZoom() {
    this.timeline.redraw()
    this.timeline.fit()
  }

  removeCollisionsAndMarkers() {
    const markers = this.timelineItems
      .get({
        filter: item => item.className.indexOf('marker') > -1
      })
      .map(item => item.id)

    this.timelineItems.remove(markers, 'internal')

    const collisions = this.timelineItems.get({
      filter: item => item.className.indexOf('collision') > -1
    })
    collisions.forEach(x => {
      x.className = ''
    })

    this.timelineItems.update(collisions, 'internal')
  }

  markCollisionsAndGaps() {
    if (this.timelineItems.length < 1) {
      return
    }

    this.removeCollisionsAndMarkers()

    const data = this.timelineItems.get({
      filter: item => item.className.indexOf('marker') === -1,
      order: (a, b) => {
        return a.start < b.start
          ? -1
          : a.start === b.start && a.end < b.end
            ? -1
            : a.start === b.start && a.end === b.end
              ? 0
              : 1
      }
    })

    const collisions = data.filter(x => this.getCollisions(x, data).length > 0)

    if (collisions.length > 0) {
      collisions.forEach(x => (x.className = 'collision'))
      this.timelineItems.update(collisions, 'internal')
    }

    const gaps: vis.TimelineItem[] = []

    data.forEach((item, idx) => {
      const next = data[idx + 1]

      if (!next) {
        return
      }

      const endDate = moment(item.end)
      const nextStartDate = moment(next.start)

      if (
        !endDate.add(1, 'days').startOf('day').isSame(nextStartDate) &&
        endDate.isBefore(nextStartDate)
      ) {
        const newGap: vis.TimelineItem = {
          id: 'gap_' + this.ordinal++,
          start: endDate.startOf('day').add(1, 'days').toDate(),
          end: nextStartDate.add(-1, 'days').endOf('day').toDate(),
          content: '',
          className: 'marker gap'
        }

        // can't be a gap if it collides with a fee schedule
        if (this.getCollisions(newGap, data).length > 0) {
          return
        }

        newGap.type = 'background'
        newGap.content = this.getFormattedGapContent(newGap.start, newGap.end)

        gaps.push(newGap)
      }
    })

    this.timelineItems.add(gaps, 'internal')
  }

  private getCollisions(x, data) {
    const xStart = moment(x.start)
    const xEnd = moment(x.end)

    return data.filter(y => {
      const yStart = moment(y.start)
      const yEnd = moment(y.end)

      return (
        x !== y &&
        xStart.isBefore(yEnd) &&
        yStart.isBefore(xEnd) &&
        x.type !== 'background' &&
        y.type !== 'background'
      )
    })
  }

  private convertFeeScheduleToTimeline(egfs: IEntityGroupFeeSchedule): IFeeScheduleTimelineItem {
    const start = moment.utc(egfs.Start).startOf('day').toISOString()
    const end = moment.utc(egfs.End).endOf('day').toISOString()
    const minDate = moment.utc(egfs.FeeSchedule.ActiveOnDateTime).startOf('day').toISOString()
    const maxDate = moment.utc(egfs.FeeSchedule.ExpiresOnDateTime).endOf('day').toISOString()

    return {
      id: egfs.Id,
      start,
      end,
      minDate,
      maxDate,
      content: this.getFormattedContent(egfs.FeeSchedule.Name, start, end),
      className: ''
    } as IFeeScheduleTimelineItem
  }

  private resetSelectedItem() {
    this.selectedItem = {
      id: null,
      content: null,
      start: null,
      end: null,
      className: null
    }
  }

  private getFormattedContent(name, startDate, endDate) {
    return `${name} (${moment.utc(startDate).format('MM/DD/YYYY')} - ${moment
      .utc(endDate)
      .format('MM/DD/YYYY')})`
  }

  private getFormattedGapContent(startDate, endDate) {
    const formatted = {
      start: moment(startDate).format('MM/DD/YYYY'),
      end: moment(endDate).format('MM/DD/YYYY')
    }

    return `<i class="fa fa-exclamation-triangle"></i> Gap (${formatted.start} - ${formatted.end})`
  }
}
